// src/contexts/ToastContext.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTheme } from './ThemeContext';
import ToastContainer, {
  type ToastItem,
  type ToastVariant,
} from '../components/common/Toast';
import { logger } from '@/utils/logger';

interface ToastContextValue {
  show: (message: string, variant: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// Singleton bridge para callers fora da árvore React (ex: api.ts)
let toastApi: ToastContextValue | null = null;

const VARIANT_DEFAULT_DURATION_MS: Record<ToastVariant, number | null> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: null, // persistente até clique (decisão UX 3.7 — erros não desaparecem antes de leitura)
};

let nextToastId = 0;

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const { theme } = useTheme();
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((message: string, variant: ToastVariant) => {
    const id = nextToastId++;
    const duration = VARIANT_DEFAULT_DURATION_MS[variant];
    setToasts((list) => [...list, { id, message, variant, duration }]);
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ show }), [show]);

  useEffect(() => {
    toastApi = value;
    return () => {
      if (toastApi === value) {
        toastApi = null;
      }
    };
  }, [value]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} theme={theme} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast deve ser usado dentro de um ToastProvider');
  return ctx;
};

// API estática imperativa (callable de qualquer lugar, inclusive não-React)
const callToast = (message: string, variant: ToastVariant) => {
  if (!toastApi) {
    logger.warn(`Toast.${variant} chamado antes do ToastProvider montar; mensagem: ${message}`);
    return;
  }
  toastApi.show(message, variant);
};

export const Toast = {
  success: (message: string) => callToast(message, 'success'),
  error: (message: string) => callToast(message, 'error'),
  info: (message: string) => callToast(message, 'info'),
  warning: (message: string) => callToast(message, 'warning'),
};

// Dev-only debug export — strip em prod via import.meta.env.DEV
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as Window & { Toast?: typeof Toast }).Toast = Toast;
}
